import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertTriangle, Download, ExternalLink, Wifi, CheckCircle2, Loader2,
  Printer, Play, Plus,
} from "lucide-react";
import { savePrinter, saveMethod } from "@/utils/printService";
import { toast } from "sonner";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export function QZTraySection({
  qzStatus, qzError, printers, testPrinting, selectedPrinter, setSelectedPrinter,
  tryConnect, testPrint, installerDownloading, downloadInstaller,
  printStations, savingStations, addStation, removeStation, updateStation, saveStations,
}) {
  return (
    <div className="space-y-4">
      <Card className="border-l-4 border-l-amber-500">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-700">Se voce ja usa Menu Integrado com QZ Tray</p>
              <p className="text-xs text-amber-600">O instalador v10 usa trustedRootCert + QZ_OPTS para impressao 100% silenciosa. Compativel com Menu Integrado.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
        <p className="text-sm font-bold text-blue-800">Requisitos:</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-lg p-3 border border-blue-100 text-center space-y-1">
            <p className="text-xs font-bold text-gray-700">1. Java (JRE)</p>
            <a href="https://www.java.com/download/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 font-bold hover:underline">
              <Download className="w-3 h-3" /> Baixar Java <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-100 text-center space-y-1">
            <p className="text-xs font-bold text-gray-700">2. QZ Tray</p>
            <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 font-bold hover:underline">
              <Download className="w-3 h-3" /> Baixar QZ Tray <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="bg-white rounded-lg p-3 border border-blue-100 text-center space-y-1">
            <p className="text-xs font-bold text-gray-700">3. Certificado</p>
            <button onClick={() => downloadInstaller("windows")} disabled={installerDownloading}
              className="inline-flex items-center gap-1 text-[11px] text-blue-600 font-bold hover:underline disabled:opacity-50"
              data-testid="download-installer-btn">
              {installerDownloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Instalador v10
            </button>
          </div>
        </div>
      </div>

      <Card className={`border-l-4 ${qzStatus === "connected" ? "border-l-emerald-500" : "border-l-blue-500"}`}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wifi className="w-5 h-5" /> Conectar QZ Tray
            {qzStatus === "connected" && <Badge className="bg-emerald-100 text-emerald-700 ml-auto text-[10px]">Conectado</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-0">
          <div className="flex items-center gap-3">
            <Button
              onClick={tryConnect}
              disabled={qzStatus === "connecting"}
              className="gap-2 h-11 font-bold bg-blue-500 hover:bg-blue-600"
              data-testid="connect-qz-btn"
            >
              {qzStatus === "connecting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
              {qzStatus === "connected" ? "RECONECTAR" : "CONECTAR AO QZ TRAY"}
            </Button>
            {qzStatus === "connected" && (
              <span className="text-sm text-emerald-600 font-bold flex items-center gap-1">
                <CheckCircle2 className="w-5 h-5" /> Conectado!
              </span>
            )}
          </div>
          {qzError && (
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 inline mr-2" /> {qzError}
            </div>
          )}
          {printers.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-bold text-gray-700">Selecione sua impressora termica:</p>
              <div className="space-y-1">
                {printers.map((p, i) => {
                  const isSelected = selectedPrinter === p;
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setSelectedPrinter(p);
                        savePrinter(p);
                        saveMethod("qz");
                        toast.success(`Impressora "${p}" selecionada e salva!`);
                      }}
                      className={`w-full flex items-center gap-2 p-3 rounded-lg text-sm text-left transition-all ${
                        isSelected
                          ? "bg-emerald-50 border-2 border-emerald-500 ring-2 ring-emerald-200"
                          : "bg-white border border-gray-200 hover:border-blue-400 hover:bg-blue-50"
                      }`}
                      data-testid={`select-printer-${i}`}
                    >
                      <Printer className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-emerald-600" : "text-gray-400"}`} />
                      <span className={`flex-1 ${isSelected ? "font-bold text-emerald-800" : ""}`}>{p}</span>
                      {isSelected && <CheckCircle2 className="w-5 h-5 text-emerald-600" />}
                    </button>
                  );
                })}
              </div>
              {selectedPrinter && (
                <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg">
                  <p className="text-xs text-emerald-700 font-bold">Impressora salva: {selectedPrinter}</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Ao imprimir, o recibo vai direto para esta impressora. Sem dialogo do Chrome.</p>
                </div>
              )}
            </div>
          )}
          {qzStatus === "connected" && (
            <Button
              onClick={testPrint}
              disabled={testPrinting}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2 h-11 font-bold"
              data-testid="test-print-btn"
            >
              {testPrinting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
              IMPRIMIR TESTE
            </Button>
          )}
        </CardContent>
      </Card>

      {qzStatus === "connected" && (
        <Card className="border-l-4 border-l-blue-500" data-testid="print-stations-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Printer className="w-5 h-5" /> Estacoes de Impressao
              <Badge className="ml-auto text-[10px]">{printStations.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <p className="text-xs text-gray-500">Configure suas impressoras como no Menu Integrado: caixa, cozinha, etc. Cada estacao pode ter configuracoes diferentes.</p>

            {printStations.map((station, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50" data-testid={`station-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">Estacao {idx + 1}</span>
                  <Button variant="ghost" size="sm" className="text-red-500 text-xs h-7" onClick={() => removeStation(idx)} data-testid={`remove-station-${idx}`}>Remover</Button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase">Impressora</label>
                    <select value={station.printer_name} onChange={e => updateStation(idx, "printer_name", e.target.value)}
                      className="w-full text-sm border border-gray-300 rounded-md p-2 mt-0.5 bg-white" data-testid={`station-printer-${idx}`}>
                      {printers.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase">Alias</label>
                    <input type="text" value={station.alias} onChange={e => updateStation(idx, "alias", e.target.value)}
                      placeholder="ex: caixa, cucina" className="w-full text-sm border border-gray-300 rounded-md p-2 mt-0.5" data-testid={`station-alias-${idx}`} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase">Colunas</label>
                    <input type="number" value={station.columns} onChange={e => updateStation(idx, "columns", parseInt(e.target.value) || 38)}
                      className="w-full text-sm border border-gray-300 rounded-md p-2 mt-0.5" data-testid={`station-columns-${idx}`} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 uppercase">Template</label>
                    <select value={station.template} onChange={e => updateStation(idx, "template", e.target.value)}
                      className="w-full text-sm border border-gray-300 rounded-md p-2 mt-0.5 bg-white" data-testid={`station-template-${idx}`}>
                      <option value="completo">Completo</option>
                      <option value="cocina">Cozinha</option>
                      <option value="resumido">Resumido</option>
                    </select>
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {[
                    ["auto_print", "Imprimir automaticamente"],
                    ["show_store_name", "Nome da loja"],
                    ["show_order_number", "Numero do pedido"],
                    ["show_order_channel", "Canal do pedido"],
                    ["show_delivery_info", "Dados de entrega"],
                    ["show_cif", "CIF"],
                  ].map(([field, label]) => (
                    <label key={field} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={station[field] || false}
                        onChange={e => updateStation(idx, field, e.target.checked)}
                        className="w-3.5 h-3.5 rounded accent-emerald-600" />
                      <span className="text-xs text-gray-600">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => addStation(printers)} data-testid="add-station-btn">
                <Plus className="w-3.5 h-3.5" /> Adicionar Estacao
              </Button>
              {printStations.length > 0 && (
                <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700" onClick={saveStations} disabled={savingStations} data-testid="save-stations-btn">
                  {savingStations ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Salvar Estacoes
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
